import { useState } from 'react';

import { Heading, Modal, Paragraph } from '@zillow/constellation-10';

import { useQueryClient } from '@zg-rentals/react-query5';
import { AccountViewListingGroupType } from '@zg-rentals/ts-rental-manager-api-client';
import {
  useRentalManagerUserPropertyDetailsWebApiHandler_overview,
  useRentalManagerUserPropertyUnarchiveWebApiHandler_unarchive,
} from '@zg-rentals/ts-rental-manager-api-client-react-query';

import { useArchiveMultiplexProperty } from '../../../hooks/queries/useArchiveMultiplexProperty';
import { useArchiveProperty } from '../../../hooks/queries/useArchiveProperty';

import propertyUtils from '../../../utils/propertyUtils';

import ModalFooter from '../ModalFooter';

import type { RentalManagerAddressDto } from '@zg-rentals/ts-rental-manager-api-client';

type Props = {
  onClose: () => void;
  isArchived: boolean;
  listingAlias: string;
  groupId: string;
  address: RentalManagerAddressDto;
  listingType: AccountViewListingGroupType;
};

const FlexibleArchivePropertyModal = ({ onClose, isArchived, listingAlias, groupId, address, listingType }: Props) => {
  const queryClient = useQueryClient();

  const { formattedCompleteAddress } = propertyUtils.formatAddress({
    ...address,
  });
  const [hasArchivingError, setHasArchivingError] = useState(false);

  const targetNoun = listingType === AccountViewListingGroupType.MULTIPLEX ? 'property' : 'unit';
  const bodyText = isArchived
    ? `${formattedCompleteAddress} will be restored to your Properties page.`
    : `If you no longer own or manage ${formattedCompleteAddress}, archive it to remove it from your properties.`;
  const linkText = isArchived ? 'for rent & off market' : 'archived';
  const headerText = isArchived ? `Remove ${targetNoun} from archive?` : `When to archive a ${targetNoun}`;
  const modalHeader = `${isArchived ? 'Unarchive' : 'Archive'} ${targetNoun}`;

  // Tag-based invalidation for the properties list, multiplex units, and ZRAM options caches. The
  // shared MutationCache invalidates any query whose meta.tags intersect these. getPropertyTableUnits
  // is invalidated more precisely in onSuccess so only the affected listing's units refetch.
  const mutationMeta = {
    invalidates: ['getProperties', 'getMultiplexUnits', 'getPropertyTableUnitsList', 'zramOptions'],
    context: { api: 'rental-manager', details: 'FlexibleArchivePropertyModal' },
  };

  const { archiveProperty, isPending: isArchivingProperty } = useArchiveProperty({ meta: mutationMeta });
  const { archiveMultiplexProperty, isPending: isArchivingMultiplex } = useArchiveMultiplexProperty({
    meta: mutationMeta,
  });
  const { mutate: unarchiveProperty, isPending: isUnarchiving } =
    useRentalManagerUserPropertyUnarchiveWebApiHandler_unarchive({ meta: mutationMeta });

  const isSubmitting = isArchivingProperty || isArchivingMultiplex || isUnarchiving;

  const handleUpdateArchiveStatus = () => {
    const mutationOptions = {
      onSuccess: () => {
        // These run alongside meta.invalidates from the shared MutationCache; overlapping
        // invalidations are safe since React Query dedupes concurrent refetches of the same query.

        // The property overview query is read directly in many places without meta.tags, so it
        // isn't reachable via meta.invalidates yet; invalidate it explicitly for this alias.
        queryClient.invalidateQueries({
          queryKey: useRentalManagerUserPropertyDetailsWebApiHandler_overview.getKey({ listingAlias }),
        });

        // Only refetch the affected group's units table rather than every group's units.
        queryClient.invalidateQueries({
          predicate: ({ meta }) =>
            Boolean(meta?.tags?.includes('getPropertyTableUnits') && meta?.tags?.includes(groupId)),
        });

        onClose();
      },
      onError: () => {
        setHasArchivingError(true);
      },
    };

    if (isArchived) {
      unarchiveProperty({ postData: { listingAlias } }, mutationOptions);
      return;
    }

    if (listingType === AccountViewListingGroupType.MULTIPLEX) {
      archiveMultiplexProperty(groupId, mutationOptions);
      return;
    }

    archiveProperty(listingAlias, mutationOptions);
  };

  return (
    <Modal
      open={true}
      onOpenChange={(open) => !open && onClose()}
      size={{ base: 'fullScreen', lg: 'md' }}
      header={<Heading level={2}>{modalHeader}</Heading>}
      body={
        <div data-test="modal-body">
          <Heading level={3} textStyle="heading-sm">
            {headerText}
          </Heading>
          <Paragraph data-test="body-text" css={{ marginBlock: '400' }}>
            {bodyText}
          </Paragraph>
          {!isArchived && (
            <Paragraph data-test="isNotArchivedMessage" css={{ marginBottom: '400' }}>
              You&#39;re still able to access the property by viewing
              {` ${linkText} `}
              properties from your Properties page.
            </Paragraph>
          )}
        </div>
      }
      footer={
        <ModalFooter
          submitText={isArchived ? 'Unarchive' : 'Archive'}
          onSubmit={handleUpdateArchiveStatus}
          onCancel={() => {
            setHasArchivingError(false);
            onClose();
          }}
          hasError={hasArchivingError}
          isSubmitting={isSubmitting}
        />
      }
    />
  );
};

export default FlexibleArchivePropertyModal;
